import { CloudSession } from '@/cloud/Session';
import { SIGN_IN_STYLES, SignInScreen } from '@/cloud/SignInScreen';
import { Application, type ApplicationOptions } from './Application';

/**
 * Bootstraps the application into a DOM container and starts the main loop. Returns the running
 * {@link Application} so callers can dispose it cleanly.
 */
export function bootstrap(options: ApplicationOptions): Application {
  const app = new Application(options);
  app.start();
  return app;
}

export type BootResult =
  | { kind: 'app'; app: Application; dispose: () => void }
  | { kind: 'sign-in'; dispose: () => void };

/**
 * Boot, asking the server first whether anyone may.
 *
 * The session is probed *before* an {@link Application} is constructed, which is the difference
 * between a gate and a curtain. On a deployment with `REQUIRE_AUTH`, an anonymous visitor never
 * causes a renderer, a WebGL context, an IndexedDB connection, or a single editor command to come
 * into existence — there is nothing behind the screen to reach with an element inspector.
 *
 * Every other configuration is unchanged and does not wait on anything: a build with no API, or one
 * where sign-in is optional, gets the editor immediately. The probe cannot reject — see
 * `CloudSession.refresh` — so a server that is down degrades to the standalone tool rather than a
 * blank page.
 */
export async function bootstrapGated(options: ApplicationOptions): Promise<BootResult> {
  const session = options.session ?? new CloudSession();
  await session.refresh();

  if (session.locked) {
    injectStyles();
    const screen = new SignInScreen({
      container: options.container,
      session,
      // `?auth=cancelled` is what the OAuth callback redirects to when the consent screen is
      // dismissed, so the wall can say what happened instead of looking like a failed sign-in.
      // Passed through verbatim rather than as a boolean, so a value added to the callback later
      // reaches the screen instead of being flattened to "not cancelled" on the way.
      outcome: readAuthOutcome(),
    });
    return { kind: 'sign-in', dispose: () => screen.dispose() };
  }

  const app = bootstrap({ ...options, session });
  return { kind: 'app', app, dispose: () => app.dispose() };
}

function readAuthOutcome(): string | null {
  try {
    return new URLSearchParams(globalThis.location?.search ?? '').get('auth');
  } catch {
    return null;
  }
}

let stylesInjected = false;

function injectStyles(): void {
  if (stylesInjected || typeof document === 'undefined') return;
  stylesInjected = true;
  document.head.appendChild(document.createElement('style')).textContent = SIGN_IN_STYLES;
}
